import { CRMLeadsAdapter } from './leads/CRMLeadsAdapter';
import { SmartHubLeadsAdapter } from './leads/SmartHubLeadsAdapter';
import { SmartHubAppointmentAdapter } from './appointments/SmartHubAppointmentAdapter';
import { SmartHubFlowsAdapter } from './flows/SmartHubFlowsAdapter';
import { FrontdoorUserAdapter } from './users/FrontdoorUserAdapter';
import { FrontdoorNotificationAdapter } from './notifications/FrontdoorNotificationAdapter';
import { FrontdoorThemeAdapter } from './themes/FrontdoorThemeAdapter';
import { FrontdoorGridSettingsAdapter } from './grid-settings/FrontdoorGridSettingsAdapter';
import { FrontdoorUserSettingsAdapter } from './user-settings/FrontdoorUserSettingsAdapter';
import { SmartHubCalendarAuthAdapter } from './calendar-auth/SmartHubCalendarAuthAdapter';
import { CRMLookupsAdapter } from './lookups/CRMLookupsAdapter';
import { FrontdoorLookupsAdapter } from './lookups/FrontdoorLookupsAdapter';
import { withLogging, withCaching } from './wrappers';
import { initializeAppointmentClient } from '@blazeo.com/appointment-client';
import { configure as setupBlazeo, getConfig } from '@blazeo.com/calendar-client';
import config from '@/config';
import { getCookie } from '@/services/generic.methods';
import moment from 'moment';
import FeatureService from '@/services/feature-service';
import { FeatureKeys } from '@/services/dropdown-enums';


let currentToken = null;
let currentConsumer = null;

/** Ensures calendar-client / appointment-client auth is configured before REST calls. */
export const ensureBlazeoConfig = () => {
  if (!config.blazeoApiUrl) return;

  const token = getCookie("CRM-Auth");
  if (!token) {
    ensurePublicBlazeoConfig();
    return;
  }

  let consumer = 'smarthub';
  if (typeof window !== 'undefined') {
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/book-an-appointment') || path.includes('/reschedule-an-appointment')) {
      consumer = 'bookingpage';
    }
  }
  const isConfigured =
    getConfig()?.baseUrl && token === currentToken && consumer === currentConsumer;
  if (isConfigured) return;

  initializeAppointmentClient({
    baseUrl: config.blazeoApiUrl,
    consumer: consumer,
    accessToken: token,
    crmApiUrl: config.apiUrl
  });

  setupBlazeo({
    baseUrl: config.blazeoApiUrl,
    accessToken: token,
    consumer: consumer,
    getDefaultOffset: () => moment().utcOffset(),
  });
  currentToken = token;
  currentConsumer = consumer;
};

/**
 * Initializes blazeo SDK with just the baseUrl and no auth token.
 * Safe to call on public/unauthenticated pages so FlowModel.getPublic works.
 * Will not overwrite an already-authenticated config.
 */
export const ensurePublicBlazeoConfig = () => {
  if (!config.blazeoApiUrl) return;
  // If already configured (e.g. by an authenticated session), don't overwrite
  if (getConfig()?.baseUrl) return;

  initializeAppointmentClient({
    baseUrl: config.blazeoApiUrl,
    consumer: 'bookingpage',
    accessToken: '',
    crmApiUrl: config.apiUrl
  });

  setupBlazeo({
    baseUrl: config.blazeoApiUrl,
    accessToken: '',
    consumer: 'bookingpage',
    getDefaultOffset: () => moment().utcOffset(),
  });
};

class DataClientRegistry {
    constructor(planOrFeatures, options = {}) {
        this.options = options; // { companyKey, ... }
        this.cache = {};

        if (planOrFeatures instanceof Set) {
            this.isBasic = !FeatureService.hasFeature(planOrFeatures, FeatureKeys.CRM);
        } else if (Array.isArray(planOrFeatures)) {
            const resolvedSet = FeatureService.resolveFeatures({ features: planOrFeatures });
            this.isBasic = !FeatureService.hasFeature(resolvedSet, FeatureKeys.CRM);
        } else {
            this.isBasic = planOrFeatures === 'basic';
        }
    }

    /**
     * A unified factory resolver that prevents boilerplate for new modules.
     * It handles lazy-loading, plan selection, and logging.
     */
    _resolveModule(moduleName, ProAdapterClass, BasicAdapterClass) {
        if (!this.cache[moduleName]) {
            const AdapterClass = this.isBasic ? BasicAdapterClass : ProAdapterClass;
            this.cache[moduleName] = withLogging(new AdapterClass(this.options), moduleName);
        }
        return this.cache[moduleName];
    }

    /**
     * Resolves a plan-agnostic (global) module that is always the same
     * adapter regardless of the user's plan (e.g. Frontdoor REST modules).
     */
    _resolveGlobal(moduleName, AdapterClass) {
        if (!this.cache[moduleName]) {
            this.cache[moduleName] = withLogging(new AdapterClass(this.options), moduleName);
        }
        return this.cache[moduleName];
    }

    // ------------------------------------------------------------------
    // FEATURE REGISTRY
    // To add a new feature (like Flows), just add a 3-line getter below:
    // ------------------------------------------------------------------

    get leads() {
        return this._resolveModule('Leads', CRMLeadsAdapter, SmartHubLeadsAdapter);
    }

    get lookups() {
        if (!this.cache['Lookups']) {
            const AdapterClass = this.isBasic ? FrontdoorLookupsAdapter : CRMLookupsAdapter;
            // const instance = new AdapterClass(this.options);
            const instance = new FrontdoorLookupsAdapter(this.options);
            // Apply caching first, then logging (so we log when cache misses or hits, or wrap caching around logged actions)
            // Let's cache the logged methods, or log the cached methods. Typically, withLogging(withCaching(instance)) will log the actual client-facing calls (including cache hits/misses).
            this.cache['Lookups'] = withLogging(withCaching(instance), 'Lookups');
        }
        return this.cache['Lookups'];
    }

    get appointments() {
        return this._resolveGlobal('Appointments', SmartHubAppointmentAdapter);
        // return this._resolveModule('Appointments', CRMAppointmentAdapter, SmartHubAppointmentAdapter);
    }

    /**
     * Flows module - supports both Pro (GraphQL) and Basic (Blazeo) plans
     */
    get flows() {
        return this._resolveGlobal('Flows', SmartHubFlowsAdapter);
    }

    // ------------------------------------------------------------------
    // FRONTDOOR (Plan-Agnostic Global Platform Modules)
    // These always use the Frontdoor REST API regardless of user plan.
    // ------------------------------------------------------------------

    get users() {
        return this._resolveGlobal('Users', FrontdoorUserAdapter);
    }

    get notifications() {
        return this._resolveGlobal('Notifications', FrontdoorNotificationAdapter);
    }

    get themes() {
        return this._resolveGlobal('Themes', FrontdoorThemeAdapter);
    }

    get CalendarAuth() {
        return this._resolveGlobal('CalendarAuth', SmartHubCalendarAuthAdapter);
    }

    get gridSettings() {
        return this._resolveGlobal('GridSettings', FrontdoorGridSettingsAdapter);
    }

    get userSettings() {
        return this._resolveGlobal('UserSettings', FrontdoorUserSettingsAdapter);
    }

    /*
    get flows() {
        return this._resolveModule('Flows', CRMFlowsAdapter, SmartHubFlowsAdapter, GqlClient, FlowModel);
    }
    */
}

export const createDataClient = (plan, options = {}) => {
    ensureBlazeoConfig();
    return new DataClientRegistry(plan, options);
};

/**
 * Creates a data client for unauthenticated/public pages.
 * Uses ensurePublicBlazeoConfig so public endpoints (e.g. getPublicFlow) work
 * without a CRM-Auth cookie.
 */
export const createPublicDataClient = (plan) => {
    
    ensurePublicBlazeoConfig();
    return new DataClientRegistry(plan || "pro", {});
};
